import React, { useMemo } from "react";
import { Image, Platform, View } from "react-native";
import { SvgXml } from "react-native-svg";

// Inline SVG illustration for a video thumbnail.
// Matches the design bundle's videos.jsx icon set.
//
// Palette: [bg, mid, light]. Background gradient runs light → bg.

export type VideoIcon =
  | "cookies"
  | "letterB"
  | "rainbow"
  | "shapes"
  | "hands"
  | "moon"
  | "mountain"
  | "pencil"
  | "generic";

export type VideoPalette = [string, string, string];

// Heuristic: pick a palette + icon from a category/title pair. The Kidly backend
// only stores categories + a thumbUrl, so for now we render this inline art
// instead of a remote thumbnail.
export function paletteFor(category: string): {
  palette: VideoPalette;
  icon: VideoIcon;
} {
  const c = (category || "").toLowerCase();
  if (c.includes("number"))
    return { palette: ["#FFB774", "#FF8A4C", "#FFE2C2"], icon: "cookies" };
  if (c.includes("letter"))
    return { palette: ["#9DBDFF", "#5B8DEF", "#DCE7FF"], icon: "letterB" };
  if (c.includes("color"))
    return { palette: ["#F492C0", "#E96BB8", "#FFD8E9"], icon: "rainbow" };
  if (c.includes("shape"))
    return { palette: ["#9CDB7E", "#7AC74F", "#DEF1CF"], icon: "shapes" };
  if (c.includes("social"))
    return { palette: ["#B5A8E8", "#8C7DD9", "#E6E0F7"], icon: "hands" };
  if (c.includes("bed") || c.includes("sleep"))
    return { palette: ["#5C5A8C", "#3A386B", "#9D9BC8"], icon: "moon" };
  if (c.includes("language"))
    return { palette: ["#FFD06B", "#E8B73D", "#FFEAB8"], icon: "mountain" };
  if (c.includes("creativ"))
    return { palette: ["#5BC0BE", "#2E9594", "#BDE7E6"], icon: "pencil" };
  return { palette: ["#FFB774", "#FF8A4C", "#FFE2C2"], icon: "generic" };
}

function buildVideoArtSvg(icon: VideoIcon, palette: VideoPalette): string {
  const [bg, , light] = palette;
  const W = 200;
  const H = 124;
  const cx = W / 2;
  const cy = H / 2;

  const gradientId = `vg-${icon}-${bg.replace("#", "")}`;

  const headerDefs =
    `<defs>` +
    `<linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${light}"/>` +
    `<stop offset="1" stop-color="${bg}"/>` +
    `</linearGradient>` +
    `</defs>`;

  const baseRect = `<rect width="${W}" height="${H}" fill="url(#${gradientId})"/>`;

  const bgCircles =
    `<circle cx="${W * 0.12}" cy="${H * 0.18}" r="${H * 0.08}" fill="rgba(255,255,255,0.35)"/>` +
    `<circle cx="${W * 0.88}" cy="${H * 0.78}" r="${H * 0.1}" fill="rgba(255,255,255,0.25)"/>`;

  let shape = "";

  if (icon === "cookies") {
    const cookie = (x: number, y: number, r: number) =>
      `<g transform="translate(${x},${y})">` +
      `<circle r="${r}" fill="#C58850"/>` +
      `<circle r="${r - 4}" fill="#E1A668"/>` +
      `<circle cx="${(Math.cos(0) * r) / 2}" cy="${(Math.sin(0) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `<circle cx="${(Math.cos(2.1) * r) / 2}" cy="${(Math.sin(2.1) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `<circle cx="${(Math.cos(4.2) * r) / 2}" cy="${(Math.sin(4.2) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `</g>`;
    shape =
      cookie(cx - 36, cy + 6, 16) +
      cookie(cx + 6, cy - 8, 18) +
      cookie(cx + 32, cy + 14, 14);
  } else if (icon === "letterB") {
    shape = `<text x="${cx}" y="${cy + 24}" font-family="Nunito, sans-serif" font-weight="900" font-size="84" text-anchor="middle" fill="#fff">B</text>`;
  } else if (icon === "rainbow") {
    const arc = (r: number, c: string) =>
      `<path d="M ${cx - r} ${cy + 14} A ${r} ${r} 0 0 1 ${cx + r} ${cy + 14}" stroke="${c}" stroke-width="8" fill="none" stroke-linecap="round"/>`;
    shape = arc(40, "#FF6B6B") + arc(30, "#FFD86B") + arc(20, "#7AC74F");
  } else if (icon === "shapes") {
    shape =
      `<circle cx="${cx - 30}" cy="${cy}" r="16" fill="#FF8A4C"/>` +
      `<rect x="${cx - 8}" y="${cy - 16}" width="32" height="32" rx="4" fill="#5B8DEF"/>` +
      `<polygon points="${cx + 44},${cy - 16} ${cx + 60},${cy + 16} ${cx + 28},${cy + 16}" fill="#F18EB6"/>`;
  } else if (icon === "hands") {
    shape =
      `<circle cx="${cx}" cy="${cy + 10}" r="10" fill="#FFD86B"/>` +
      `<circle cx="${cx - 20}" cy="${cy - 6}" r="14" fill="#fff" stroke="#2E2316" stroke-width="2"/>` +
      `<circle cx="${cx + 20}" cy="${cy - 6}" r="14" fill="#fff" stroke="#2E2316" stroke-width="2"/>`;
  } else if (icon === "moon") {
    shape =
      `<circle cx="${cx + 8}" cy="${cy - 4}" r="26" fill="#FFE9B0"/>` +
      `<circle cx="${cx + 18}" cy="${cy - 10}" r="26" fill="${bg}"/>` +
      `<circle cx="${cx - 36}" cy="${cy - 22}" r="1.6" fill="#fff"/>` +
      `<circle cx="${cx + 40}" cy="${cy + 24}" r="1.4" fill="#fff"/>` +
      `<circle cx="${cx - 18}" cy="${cy + 30}" r="1.2" fill="#fff"/>`;
  } else if (icon === "mountain") {
    shape =
      `<polygon points="${cx - 50},${cy + 22} ${cx - 14},${cy - 26} ${cx + 12},${cy + 22}" fill="#3D6E70"/>` +
      `<polygon points="${cx - 6},${cy + 22} ${cx + 26},${cy - 16} ${cx + 54},${cy + 22}" fill="#5B8B89"/>` +
      `<polygon points="${cx - 14},${cy - 26} ${cx - 18},${cy - 14} ${cx - 6},${cy - 14}" fill="#fff"/>`;
  } else if (icon === "pencil") {
    shape =
      `<g transform="rotate(-30 ${cx} ${cy})">` +
      `<rect x="${cx - 50}" y="${cy - 8}" width="10" height="16" fill="#FF6B6B"/>` +
      `<rect x="${cx - 40}" y="${cy - 8}" width="68" height="16" fill="#FFD86B"/>` +
      `<polygon points="${cx + 28},${cy - 8} ${cx + 44},${cy} ${cx + 28},${cy + 8}" fill="#2E2316"/>` +
      `</g>`;
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" preserveAspectRatio="xMidYMid slice">` +
    headerDefs +
    baseRect +
    bgCircles +
    shape +
    `</svg>`
  );
}

type VideoArtProps = {
  icon?: VideoIcon;
  palette?: VideoPalette;
  category?: string; // alternative to icon/palette
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
};

export function VideoArt({
  icon,
  palette,
  category,
  width = "100%",
  height = "100%",
  borderRadius,
}: VideoArtProps) {
  const resolved = useMemo(() => {
    if (icon && palette) return { icon, palette };
    return paletteFor(category || "");
  }, [icon, palette, category]);

  const svg = useMemo(
    () => buildVideoArtSvg(resolved.icon, resolved.palette),
    [resolved],
  );

  if (Platform.OS === "web") {
    const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return (
      <Image
        source={{ uri }}
        style={{
          width: width as any,
          height: height as any,
          borderRadius,
        }}
        resizeMode="cover"
      />
    );
  }

  return (
    <View
      style={{
        width: width as any,
        height: height as any,
        borderRadius,
        overflow: "hidden",
      }}
    >
      <SvgXml
        xml={svg}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
      />
    </View>
  );
}

export default VideoArt;
